// Product panels — faithful dark-theme miniatures of the real Orkestra screens.
//
// R3 fidelity: every panel renders in the app's real DARK theme (each is wrapped
// in data-theme="dark", so the shared design tokens resolve to the exact product
// values) inside an AppScreen shell that mirrors the real app chrome — a slim
// nav rail (real sidebar tokens), a page top-bar, real stat rows, real filter
// chips, real status colors, and realistic density. Structure/labels/statuses
// are mirrored from the actual components (FieldHub, Invoices/InvoiceDetail,
// Inbox, Deals, Analytics, AutomationHealth, FleetPage, GuestVideoRoom). Data is
// the fictional "Riverside Field Services" world. Nothing invented.
//
// GATE: no hex/rgba literals in style={{…}} — every color is a var(--token).
import React from 'react'
import {
  Mail, Phone, Globe, MessageSquare, Truck, Wrench, Check, AlertTriangle,
  Send, Zap, TrendingUp, ChevronRight,
  LayoutDashboard, CalendarRange, Receipt, BarChart3, Sparkles,
  Plus, Hash, Search, Paperclip, CheckCircle2,
} from 'lucide-react'
import {
  JOBS, JOB_STATUS_META, byId, INVOICE, INVOICE_STATS, CONVERSATIONS, CHAT,
  FLEET, FLEET_STATS, REVENUE_SERIES, ANALYTICS_STATS, PIPELINE, SWEEPS,
  PROCESSOR_RUNS, HEARTBEAT, FIELD_STATS, FIELD_VIEWS, FIELD_FILTERS, COMPANY, TEAM,
  type JobStatus,
} from './fixtures'
import { Avatar } from './primitives'

// ─── shared atoms ───────────────────────────────────────────────────────────
const card: React.CSSProperties = { background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', borderRadius: 10 }
const label: React.CSSProperties = { fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-tertiary)' }
const money = (n: number) => `$${n.toLocaleString('en-US')}`

function Pill({ text, color, bg = 'var(--surface-sunken)', dot }: { text: string; color: string; bg?: string; dot?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, color, background: bg }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />}{text}
    </span>
  )
}
const JobPill = ({ status }: { status: JobStatus }) => {
  const m = JOB_STATUS_META[status]; return <Pill text={m.label} color={m.token} dot />
}

// The app nav rail (slim). Uses the real --sidebar-* tokens (constant dark chrome).
const RAIL: { key: string; icon: React.ReactNode }[] = [
  { key: 'dashboard', icon: <LayoutDashboard size={17} /> },
  { key: 'comms', icon: <MessageSquare size={17} /> },
  { key: 'field', icon: <CalendarRange size={17} /> },
  { key: 'sales', icon: <TrendingUp size={17} /> },
  { key: 'billing', icon: <Receipt size={17} /> },
  { key: 'fleet', icon: <Truck size={17} /> },
  { key: 'reporting', icon: <BarChart3 size={17} /> },
  { key: 'ai', icon: <Sparkles size={17} /> },
]
function NavRail({ active }: { active: string }) {
  const me = TEAM[0]
  return (
    <div style={{ width: 54, flexShrink: 0, background: 'var(--sidebar-bg)', borderRight: '1px solid var(--sidebar-border)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 12, paddingBottom: 12, gap: 5 }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8, boxShadow: 'var(--logo-glow)' }}>
        <Zap size={16} style={{ color: 'var(--accent-ink)' }} />
      </div>
      {RAIL.map(r => {
        const on = r.key === active
        return (
          <div key={r.key} style={{ width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', color: on ? 'var(--sidebar-icon-active)' : 'var(--sidebar-ink)', background: on ? 'var(--sidebar-bg-active)' : 'transparent' }}>
            {r.icon}
          </div>
        )
      })}
      <div style={{ marginTop: 'auto', width: 28, height: 28, borderRadius: '50%', background: me.grad, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'var(--accent-ink)' }}>{me.initials}</div>
    </div>
  )
}

function TopBar({ title, sub, action }: { title: string; sub?: string; action?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink-primary)', letterSpacing: '-0.01em' }}>{title}</div>
        {sub && <div style={{ fontSize: 11.5, color: 'var(--ink-tertiary)', marginTop: 1 }}>{sub}</div>}
      </div>
      {action && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, padding: '6px 11px', borderRadius: 8, background: 'var(--accent)', color: 'var(--accent-ink)' }}>
          <Plus size={13} /> {action}
        </span>
      )}
    </div>
  )
}

// The dark app shell every panel sits in.
function AppScreen({ active, title, sub, action, children, rail = true, pad = 14 }: {
  active: string; title?: string; sub?: string; action?: string; children: React.ReactNode; rail?: boolean; pad?: number
}) {
  return (
    <div data-theme="dark" style={{ display: 'flex', background: 'var(--canvas)', color: 'var(--ink-primary)' }}>
      {rail && <NavRail active={active} />}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {title && <TopBar title={title} sub={sub} action={action} />}
        <div style={{ padding: pad, flex: 1 }}>{children}</div>
      </div>
    </div>
  )
}

function StatRow({ stats }: { stats: { label: string; value: string; tone?: string }[] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${stats.length}, 1fr)`, gap: 7, marginBottom: 12 }}>
      {stats.map(s => (
        <div key={s.label} style={{ ...card, padding: '9px 11px' }}>
          <div style={label}>{s.label}</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: s.tone ?? 'var(--ink-primary)', marginTop: 3 }}>{s.value}</div>
        </div>
      ))}
    </div>
  )
}
function Chips({ items, active = 0 }: { items: string[]; active?: number }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
      {items.map((t, i) => (
        <span key={t} style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 999, color: i === active ? 'var(--accent-ink)' : 'var(--ink-secondary)', background: i === active ? 'var(--accent)' : 'var(--surface-sunken)' }}>{t}</span>
      ))}
    </div>
  )
}

// ─── 1. Dispatch board (Field Hub) ──────────────────────────────────────────
export function DispatchBoard({ compact = false }: { compact?: boolean }) {
  const rows = compact ? JOBS.slice(0, 6) : JOBS
  return (
    <AppScreen active="field" rail={!compact} title="Field Hub" sub={`${COMPANY.name} · 6 active · 25 scheduled`} action="New job" pad={14}>
      {!compact && <StatRow stats={FIELD_STATS} />}
      {!compact && <Chips items={FIELD_VIEWS} active={0} />}
      <Chips items={FIELD_FILTERS} active={0} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginBottom: 10 }}>
        {(['scheduled', 'en_route', 'on_site', 'complete', 'pending_parts', 'awaiting_approval'] as JobStatus[]).map(s => (
          <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'var(--ink-tertiary)' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: JOB_STATUS_META[s].token }} />{JOB_STATUS_META[s].label}
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map(j => {
          const tech = byId(j.tech)
          return (
            <div key={j.id} style={{ ...card, padding: '9px 11px', display: 'grid', gridTemplateColumns: '40px 1fr auto', gap: 11, alignItems: 'center', borderLeft: `3px solid ${JOB_STATUS_META[j.status].token}` }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink-secondary)', fontFamily: "'JetBrains Mono', monospace" }}>{j.time}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.customer}</span>
                  {j.priority === 'urgent' && <Pill text="URGENT" color="var(--danger)" bg="var(--danger-dim)" />}
                  {j.conflict && <span title="Scheduling conflict" style={{ display: 'inline-flex', color: 'var(--conflict-indicator)' }}><AlertTriangle size={12} /></span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.service} · {j.site}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <JobPill status={j.status} />
                <Avatar initials={tech.initials} grad={tech.grad} size={22} />
              </div>
            </div>
          )
        })}
      </div>
    </AppScreen>
  )
}

// ─── 2. Invoice (InvoiceDetail) ─────────────────────────────────────────────
export function InvoicePanel() {
  const sub = INVOICE.lines.reduce((s, l) => s + l.qty * l.unit, 0)
  const tax = Math.round(sub * INVOICE.taxRate)
  const total = sub + tax
  return (
    <AppScreen active="billing" title="Invoices" sub={`${COMPANY.name} · Accounting`} action="New invoice" pad={14}>
      <StatRow stats={INVOICE_STATS} />
      <div style={{ ...card, padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-primary)', fontFamily: "'JetBrains Mono', monospace" }}>{INVOICE.number}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-tertiary)', marginTop: 2 }}>Bill to {INVOICE.contact} · {INVOICE.customer}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Pill text={INVOICE.status} color="var(--info)" bg="var(--info-dim)" />
            <div style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 5 }}>{INVOICE.terms} · due {INVOICE.due}</div>
          </div>
        </div>
        <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
          {INVOICE.lines.map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, padding: '10px 12px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
              <div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-primary)' }}>{l.desc}</div>
                <div style={{ fontSize: 10.5, color: 'var(--ink-tertiary)' }}>{l.qty} × {money(l.unit)}</div>
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-primary)', alignSelf: 'center' }}>{money(l.qty * l.unit)}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 11, display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5 }}>
          <Row k="Subtotal" v={money(sub)} />
          <Row k={`Tax · ${(INVOICE.taxRate * 100).toFixed(0)}%`} v={money(tax)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid var(--border-default)' }}>
            <span style={{ fontWeight: 800, color: 'var(--ink-primary)' }}>Total due</span>
            <span style={{ fontWeight: 800, color: 'var(--ink-primary)' }}>{money(total)}</span>
          </div>
        </div>
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', borderRadius: 8, background: 'var(--positive-dim)', border: '1px solid var(--positive-border)' }}>
          <TrendingUp size={14} style={{ color: 'var(--positive)' }} />
          <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>Job margin captured: <strong style={{ color: 'var(--positive)' }}>{INVOICE.marginPct}%</strong></span>
        </div>
      </div>
    </AppScreen>
  )
}
const Row = ({ k, v }: { k: string; v: string }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--ink-secondary)' }}><span>{k}</span><span>{v}</span></div>
)

// ─── 3. Shared inbox ─────────────────────────────────────────────────────────
const CH: Record<string, { icon: React.ReactNode; color: string }> = {
  email: { icon: <Mail size={13} />, color: 'var(--info)' },
  chat: { icon: <MessageSquare size={13} />, color: 'var(--accent-text)' },
  phone: { icon: <Phone size={13} />, color: 'var(--positive)' },
  portal: { icon: <Globe size={13} />, color: 'var(--danger)' },
}
export function InboxPanel() {
  return (
    <AppScreen active="comms" title="Inbox" sub={`${COMPANY.name} · 4 open · 2 unread`} pad={14}>
      <Chips items={['Mine', 'Unassigned', 'All', 'Team']} active={2} />
      <div style={{ display: 'flex', gap: 16, borderBottom: '1px solid var(--border-subtle)', marginBottom: 10 }}>
        {['Open', 'Pending', 'Snoozed', 'Resolved'].map((t, i) => (
          <span key={t} style={{ fontSize: 12, fontWeight: i === 0 ? 700 : 600, color: i === 0 ? 'var(--ink-primary)' : 'var(--ink-tertiary)', paddingBottom: 8, borderBottom: i === 0 ? '2px solid var(--accent)' : '2px solid transparent' }}>{t}{i === 0 ? ' · 4' : ''}</span>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {CONVERSATIONS.map(c => {
          const who = c.assignee ? byId(c.assignee) : null
          const ch = CH[c.channel]
          return (
            <div key={c.id} style={{ ...card, padding: '10px 12px', display: 'flex', gap: 11, alignItems: 'flex-start', borderLeft: c.priority === 'urgent' ? '3px solid var(--danger)' : '3px solid transparent' }}>
              <span style={{ width: 26, height: 26, borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-sunken)', color: ch.color, flexShrink: 0 }}>{ch.icon}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-primary)' }}>{c.from}</span>
                  {c.unread ? <span style={{ fontSize: 9, fontWeight: 800, minWidth: 15, height: 15, padding: '0 4px', borderRadius: 999, background: 'var(--accent)', color: 'var(--accent-ink)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{c.unread}</span> : null}
                  <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--ink-tertiary)' }}>{c.when}</span>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.subject}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                  <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{c.preview}</span>
                  {c.sla === 'breached'
                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, color: 'var(--danger)', flexShrink: 0 }}><AlertTriangle size={11} /> SLA breached</span>
                    : c.sla ? <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--warning)', flexShrink: 0 }}>{c.sla} left</span> : null}
                  {who && <Avatar initials={who.initials} grad={who.grad} size={20} />}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </AppScreen>
  )
}

// ─── 4. Team chat ────────────────────────────────────────────────────────────
export function ChatPanel() {
  return (
    <AppScreen active="comms" pad={0}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
        <Hash size={16} style={{ color: 'var(--ink-tertiary)' }} />
        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink-primary)' }}>dispatch</span>
        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>· 7 members</span>
        <Search size={14} style={{ color: 'var(--ink-tertiary)', marginLeft: 'auto' }} />
      </div>
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 15 }}>
        {CHAT.map(m => {
          const who = byId(m.who)
          return (
            <div key={m.id} style={{ display: 'flex', gap: 10 }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <Avatar initials={who.initials} grad={who.grad} size={30} />
                <span style={{ position: 'absolute', right: -1, bottom: -1, width: 9, height: 9, borderRadius: '50%', background: 'var(--positive)', border: '2px solid var(--canvas)' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{who.name}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--ink-tertiary)' }}>{m.when}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink-secondary)', lineHeight: 1.5 }}>{m.text}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 5, alignItems: 'center' }}>
                  {m.reactions?.map((r, i) => (
                    <span key={i} style={{ fontSize: 11, padding: '1px 7px', borderRadius: 999, background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', color: 'var(--ink-secondary)' }}>{r.emoji} {r.n}</span>
                  ))}
                  {m.thread && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>{m.thread} replies <ChevronRight size={12} /></span>}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <div style={{ padding: '0 16px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 10, background: 'var(--field-bg)', border: '1px solid var(--accent-border)' }}>
          <Paperclip size={15} style={{ color: 'var(--ink-tertiary)' }} />
          <span style={{ fontSize: 12.5, color: 'var(--ink-tertiary)', flex: 1 }}>Message #dispatch…</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, padding: '5px 10px', borderRadius: 7, background: 'var(--accent)', color: 'var(--accent-ink)' }}><Send size={13} /> Send</span>
        </div>
      </div>
    </AppScreen>
  )
}

// ─── 5. Orkestra Meetings ───────────────────────────────────────────────────
// The Meetings visual is a self-chromed image with AI-generated (simulated)
// participants — rendered directly in the tour (see sections.tsx) with an
// in-context disclosure, NOT a built panel. Removed the prior built
// MeetingsPanel here to avoid a superseded corpse. Owner decision: see
// LANDING_PROJECT.md decision log ("simulated participants", Jul 11).

// ─── 6. Fleet ────────────────────────────────────────────────────────────────
const VSTATUS: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: 'Active', color: 'var(--positive)', bg: 'var(--positive-dim)' },
  in_shop: { label: 'In shop', color: 'var(--warning)', bg: 'var(--warning-dim)' },
  retired: { label: 'Retired', color: 'var(--ink-tertiary)', bg: 'var(--surface-sunken)' },
}
const DUE: Record<string, { token: string; label: string }> = {
  ok: { token: 'var(--ink-tertiary)', label: '—' },
  due_soon: { token: 'var(--warning)', label: 'Due soon' },
  overdue: { token: 'var(--danger)', label: 'Overdue' },
}
export function FleetPanel() {
  return (
    <AppScreen active="fleet" title="Fleet" sub={`${COMPANY.name} · Vehicles & equipment`} action="Add vehicle" pad={14}>
      <StatRow stats={FLEET_STATS} />
      <div style={{ display: 'flex', gap: 16, borderBottom: '1px solid var(--border-subtle)', marginBottom: 10 }}>
        {['Vehicles', 'Equipment'].map((t, i) => (
          <span key={t} style={{ fontSize: 12, fontWeight: i === 0 ? 700 : 600, color: i === 0 ? 'var(--ink-primary)' : 'var(--ink-tertiary)', paddingBottom: 8, borderBottom: i === 0 ? '2px solid var(--accent)' : '2px solid transparent' }}>{t}</span>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {FLEET.map(v => {
          const st = VSTATUS[v.status]; const d = DUE[v.due]; const who = v.assigned ? byId(v.assigned) : null
          return (
            <div key={v.id} style={{ ...card, padding: '10px 12px', display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 11, alignItems: 'center' }}>
              <span style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--accent-dim)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Truck size={15} /></span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-primary)' }}>{v.name} <span style={{ fontWeight: 500, color: 'var(--ink-tertiary)' }}>· {v.plate}</span></div>
                <div style={{ fontSize: 10.5, color: 'var(--ink-tertiary)' }}>{v.make} · {v.odo} mi</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {v.due !== 'ok' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: d.token }}><Wrench size={10} /> {d.label}</span>}
                <Pill text={st.label} color={st.color} bg={st.bg} />
              </div>
              {who ? <Avatar initials={who.initials} grad={who.grad} size={22} /> : <span style={{ width: 22, textAlign: 'center', color: 'var(--ink-tertiary)', fontSize: 12 }}>—</span>}
            </div>
          )
        })}
      </div>
    </AppScreen>
  )
}

// ─── 7. Analytics ────────────────────────────────────────────────────────────
export function AnalyticsPanel() {
  const w = 320, h = 92, max = Math.max(...REVENUE_SERIES), min = Math.min(...REVENUE_SERIES)
  const pts = REVENUE_SERIES.map((v, i) => [(i / (REVENUE_SERIES.length - 1)) * w, h - ((v - min) / (max - min)) * (h - 12) - 6])
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')
  const area = `${line} L${w},${h} L0,${h} Z`
  return (
    <AppScreen active="reporting" title="Analytics" sub={`${COMPANY.name} · Last 30 days`} pad={14}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7, marginBottom: 12 }}>
        {ANALYTICS_STATS.map(s => (
          <div key={s.label} style={{ ...card, padding: '10px 11px' }}>
            <div style={label}>{s.label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 3 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)' }}>{s.value}</span>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: s.tone }}>{s.delta}</span>
            </div>
          </div>
        ))}
      </div>
      <div style={{ ...card, padding: 13, marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={label}>Revenue collected over time</span>
          <span style={{ fontSize: 10.5, color: 'var(--positive)', fontWeight: 700 }}>▲ trending up</span>
        </div>
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="ork-rev2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--positive)" stopOpacity="0.32" />
              <stop offset="100%" stopColor="var(--positive)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill="url(#ork-rev2)" />
          <path d={line} fill="none" stroke="var(--positive)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          {pts.slice(-1).map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="3.5" fill="var(--positive)" />)}
        </svg>
      </div>
      <div style={{ ...card, padding: 13 }}>
        <div style={{ ...label, marginBottom: 9 }}>Quote pipeline</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {PIPELINE.map(p => (
            <div key={p.stage} style={{ display: 'grid', gridTemplateColumns: '70px 1fr 30px', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: 'var(--ink-secondary)' }}>{p.stage}</span>
              <span style={{ height: 8, borderRadius: 999, background: 'var(--surface-sunken)', overflow: 'hidden', display: 'block' }}>
                <span style={{ display: 'block', height: '100%', width: `${p.value}%`, background: 'linear-gradient(90deg, var(--accent), var(--positive))', borderRadius: 999 }} />
              </span>
              <span style={{ fontSize: 10.5, color: 'var(--ink-tertiary)', textAlign: 'right' }}>{p.count}</span>
            </div>
          ))}
        </div>
      </div>
    </AppScreen>
  )
}

// ─── 8. Automation Health ────────────────────────────────────────────────────
export function AutomationPanel() {
  return (
    <AppScreen active="reporting" title="Automation Health" sub={`${COMPANY.name} · Owner view`} pad={14}>
      <div style={{ ...card, padding: '12px 14px', marginBottom: 10, border: '1px solid var(--positive-border)', background: 'var(--positive-dim)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--surface-raised)', color: 'var(--positive)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><CheckCircle2 size={18} /></span>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink-primary)' }}>Healthy</span>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--positive)' }} className="ork-anim-pulse" />
            <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>heartbeat {HEARTBEAT.last}</span>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-secondary)', marginTop: 2 }}>{HEARTBEAT.runs24} runs · {HEARTBEAT.failed24} failed (24h)</div>
        </div>
      </div>
      <div style={{ ...label, marginBottom: 7 }}>Sweep chains</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {SWEEPS.map(s => (
          <div key={s.name} style={{ ...card, padding: '10px 12px', display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 11, alignItems: 'center' }}>
            <span style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--positive-dim)', color: 'var(--positive)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Check size={14} /></span>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-primary)' }}>{s.name}</div>
            <span style={{ fontSize: 10.5, color: 'var(--ink-tertiary)' }}>{s.queued} queued</span>
            <Pill text="Scheduled" color="var(--positive)" bg="var(--positive-dim)" />
          </div>
        ))}
      </div>
      <div style={{ ...label, marginBottom: 7 }}>Recent processor runs</div>
      <div style={{ ...card, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 40px 40px', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-tertiary)' }}>
          <span>Ran</span><span>Duration</span><span>Sent</span><span>Fail</span>
        </div>
        {PROCESSOR_RUNS.map((r, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 40px 40px', gap: 8, padding: '8px 12px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', fontSize: 11.5, color: 'var(--ink-secondary)', alignItems: 'center' }}>
            <span>{r.ran}</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--ink-tertiary)' }}>{r.dur}</span><span style={{ color: 'var(--positive)' }}>{r.sent}</span><span>{r.failed}</span>
          </div>
        ))}
      </div>
    </AppScreen>
  )
}
